/**
 * Admin Diagnostic Tagging page.
 *
 * Route: `/admin/diagnostic-review` (admin only)
 *
 * Kick off AI batch-tagging of the most-used untagged reading/listening items.
 * Tags are published immediately without a manual review step.
 *
 * @module AdminDiagnosticReviewPage
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getRecommendationStats, startBatchTag } from "@/lib/diagnosticApi";

function RecommendationStatsCard() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["diagnostic-recommendation-stats"],
    queryFn: getRecommendationStats,
  });
  const stats = data ?? [];
  if (stats.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-ui text-base">
          {t("diagnostic.review.recoStatsTitle", {
            defaultValue: "Recommendation effectiveness",
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-1 pr-4">Tag</th>
              <th className="py-1 pr-4">Resource</th>
              <th className="py-1 pr-4">Served</th>
              <th className="py-1 pr-4">Before</th>
              <th className="py-1 pr-4">After</th>
              <th className="py-1 pr-4">Δ</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((s, i) => (
              <tr
                key={`${s.tag}-${s.resource_id}-${i}`}
                className="border-t border-border/40"
              >
                <td className="py-1 pr-4 font-mono text-xs">{s.tag}</td>
                <td className="py-1 pr-4">{s.resource_id ?? "—"}</td>
                <td className="py-1 pr-4">{s.served_count}</td>
                <td className="py-1 pr-4">
                  {s.avg_error_rate_before != null
                    ? `${Math.round(s.avg_error_rate_before * 100)}%`
                    : "—"}
                </td>
                <td className="py-1 pr-4">
                  {s.avg_error_rate_after != null
                    ? `${Math.round(s.avg_error_rate_after * 100)}%`
                    : "—"}
                </td>
                <td
                  className={
                    s.delta == null
                      ? "py-1 pr-4"
                      : s.delta < 0
                        ? "py-1 pr-4 text-emerald-600"
                        : "py-1 pr-4 text-rose-600"
                  }
                >
                  {s.delta != null
                    ? `${s.delta > 0 ? "+" : ""}${Math.round(s.delta * 100)}%`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function AdminDiagnosticReviewPage() {
  const { t } = useTranslation();
  const [module, setModule] = useState("reading");
  const [level, setLevel] = useState("");
  const [limit, setLimit] = useState(50);

  const batch = useMutation({
    mutationFn: () =>
      startBatchTag({ module, level: level || undefined, limit }),
    onSuccess: (res) => {
      toast.success(
        t("diagnostic.review.batchStarted", {
          defaultValue: "Batch tagging started for {{count}} item(s)",
          count: res.queued,
        }),
      );
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Batch tagging failed");
    },
  });

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="font-ui text-xl font-semibold text-foreground sm:text-2xl">
          {t("diagnostic.review.title", {
            defaultValue: "Diagnostic Tagging",
          })}
        </h1>
        <p className="font-ui mt-1 text-sm text-muted-foreground">
          {t("diagnostic.review.subtitle", {
            defaultValue:
              "AI-tag distractors on the most-used reading and listening items.",
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-ui text-base">
            {t("diagnostic.review.batchTitle", {
              defaultValue: "Batch-tag untagged items",
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">
              {t("diagnostic.review.module", { defaultValue: "Module" })}
            </Label>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reading">reading</SelectItem>
                <SelectItem value="listening">listening</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">
              {t("diagnostic.review.level", {
                defaultValue: "Level (optional)",
              })}
            </Label>
            <Input
              className="w-[120px]"
              placeholder="e.g. B1"
              value={level}
              onChange={(e) => setLevel(e.target.value.toUpperCase())}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">
              {t("diagnostic.review.limit", { defaultValue: "Limit" })}
            </Label>
            <Input
              className="w-[100px]"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 1)}
            />
          </div>
          <Button onClick={() => batch.mutate()} disabled={batch.isPending}>
            {batch.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("diagnostic.review.startBatch", {
              defaultValue: "Start batch tagging",
            })}
          </Button>
        </CardContent>
      </Card>

      <RecommendationStatsCard />
    </div>
  );
}
